import React from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, Shield, CloudRain, HeartPulse, Mountain, PhoneCall, Users } from "lucide-react";
import { IMAGES, GALLERY_STRIP } from "@/data/images";
import { MISSION_PROGRAM, GAME_MODULES, SCORING, UPSELLS } from "@/data/program";
import { PACKAGES, PRICE_TABLE_24, PRICE_NOTE } from "@/data/packages";
import { SITE } from "@/data/site";
import { trackEvent } from "@/lib/analytics";
import { LeadForm, Faq } from "@/components/LeadForm";

const Wrap = ({ children, cls = "" }) => (
  <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 ${cls}`}>{children}</section>
);

const Overline = ({ children }) => (
  <p className="text-xs font-mono font-bold uppercase tracking-[0.25em] text-amber">{children}</p>
);

const CtaLink = ({ to, children, variant = "primary", testId, location = "body" }) => {
  const base = "inline-flex items-center justify-center gap-2 font-display font-bold uppercase tracking-wide transition-colors";
  const styles = variant === "primary"
    ? `${base} bg-amber hover:bg-amberdark text-white px-8 py-4 text-base`
    : `${base} border border-linestrong text-white hover:border-amber hover:text-amber px-8 py-4 text-base`;
  return (
    <Link to={to} onClick={() => trackEvent("click_primary_cta", { location, label: typeof children === "string" ? children : "cta" })} className={styles} data-testid={testId}>
      {children}
    </Link>
  );
};

const Img = ({ imgKey, className = "", eager = false }) => {
  const img = IMAGES[imgKey];
  if (!img) return null;
  return (
    <img
      src={img.src}
      alt={img.alt}
      width={img.w}
      height={img.h}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      className={`object-cover ${className}`}
    />
  );
};

// ---------- Blocks ----------

const HeroBlock = ({ b }) => (
  <header className="relative overflow-hidden grain" data-testid="hero-section">
    <div className="absolute inset-0">
      <Img imgKey={b.img} eager className="w-full h-full opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
    </div>
    <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end ${b.big ? "min-h-[92svh] pb-16 pt-28" : "min-h-[70svh] pb-14 pt-28"}`}>
      <Overline>{b.overline}</Overline>
      <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-white text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-4xl text-balance">
        {b.title}
      </h1>
      {b.sub && <p className="text-base md:text-lg text-[#C6CBD4] mt-5 max-w-2xl leading-relaxed">{b.sub}</p>}
      {b.support && <p className="text-sm text-mist mt-3 max-w-2xl">{b.support}</p>}
      {b.trust && (
        <ul className="flex flex-wrap gap-2 mt-6" data-testid="hero-trust-line">
          {b.trust.map((t) => (
            <li key={t} className="text-[11px] font-mono uppercase tracking-wider text-mist border border-linestrong px-3 py-1.5 bg-ink/50">{t}</li>
          ))}
        </ul>
      )}
      {(b.primary || b.secondary) && (
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          {b.primary && <CtaLink to={b.primary.href} testId="hero-cta-primary" location="hero">{b.primary.label}</CtaLink>}
          {b.secondary && <CtaLink to={b.secondary.href} variant="ghost" testId="hero-cta-secondary" location="hero">{b.secondary.label}</CtaLink>}
        </div>
      )}
      {b.price && <p className="font-mono text-sm text-amber mt-6 font-bold" data-testid="hero-price">{b.price}</p>}
    </div>
  </header>
);

const AnswerBlock = ({ b }) => (
  <Wrap cls="border-l-2 border-amber">
    <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-white max-w-3xl">{b.q}</h2>
    <p className="text-base md:text-lg text-white/90 mt-4 max-w-3xl leading-relaxed font-medium">{b.short}</p>
    {b.paras?.map((p, i) => <p key={i} className="text-sm sm:text-base text-mist mt-3 max-w-3xl leading-relaxed">{p}</p>)}
  </Wrap>
);

const SectionBlock = ({ b }) => (
  <Wrap>
    <div className={`grid gap-10 ${b.img ? "lg:grid-cols-2 items-center" : ""}`}>
      <div className={b.reverse ? "lg:order-2" : ""}>
        {b.kicker && <Overline>{b.kicker}</Overline>}
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3 text-balance">{b.heading}</h2>
        {b.paras?.map((p, i) => <p key={i} className="text-sm sm:text-base text-mist mt-4 leading-relaxed">{p}</p>)}
        {b.list && (
          <ul className="mt-5 space-y-2.5">
            {b.list.map((li) => (
              <li key={li} className="flex items-start gap-3 text-sm sm:text-base text-mist">
                <Check className="w-4 h-4 text-amber mt-1 shrink-0" aria-hidden="true" />
                <span>{li}</span>
              </li>
            ))}
          </ul>
        )}
        {b.link && (
          <Link to={b.link.href} className="inline-flex items-center gap-2 mt-6 text-amber font-display font-bold uppercase tracking-wide hover:gap-3 transition-all" data-testid={`section-link-${b.link.href.slice(1).replace(/\//g, "-")}`}>
            {b.link.label} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        )}
      </div>
      {b.img && (
        <figure className={b.reverse ? "lg:order-1" : ""}>
          <Img imgKey={b.img} className="w-full aspect-[4/3] border border-line" />
        </figure>
      )}
    </div>
  </Wrap>
);

const StepsBlock = ({ b }) => (
  <Wrap>
    {b.kicker && <Overline>{b.kicker}</Overline>}
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">{b.heading}</h2>
    <ol className="mt-10 grid gap-px bg-line sm:grid-cols-2 lg:grid-cols-5 border border-line" data-testid="how-it-works-steps">
      {b.items.map((s) => (
        <li key={s.k} className="bg-surface p-6 flex flex-col gap-3 hover:bg-elevated transition-colors">
          <span className="font-mono text-xs text-dim">{s.k}</span>
          <span className="font-display font-extrabold uppercase tracking-wide text-amber text-xl">{s.t}</span>
          <span className="text-sm text-mist leading-relaxed">{s.d}</span>
        </li>
      ))}
    </ol>
  </Wrap>
);

const TimelineBlock = () => (
  <Wrap>
    <Overline>PROGRAM MISE</Overline>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">Vizuální časová osa Mission Ještěd</h2>
    <ol className="mt-10 relative border-l-2 border-linestrong ml-3 space-y-0" data-testid="mission-timeline">
      {MISSION_PROGRAM.map((p) => (
        <li key={p.phase} className="relative pl-8 pb-8 group" data-testid={`timeline-phase-${p.phase.toLowerCase().replace(/ /g, "-")}`}>
          <span className="absolute -left-[9px] top-1 w-4 h-4 bg-ink border-2 border-amber group-hover:bg-amber transition-colors" aria-hidden="true" />
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="font-mono text-sm font-bold text-amber bg-amber/10 px-2 py-0.5">{p.time}</span>
            <span className="font-display font-extrabold uppercase tracking-wide text-white text-xl sm:text-2xl">{p.phase}</span>
          </div>
          <p className="text-sm font-semibold text-white/80 mt-1.5">{p.title}</p>
          <p className="text-sm text-mist mt-1 max-w-2xl leading-relaxed">{p.desc}</p>
        </li>
      ))}
    </ol>
  </Wrap>
);

const GameBlock = () => (
  <Wrap>
    <Overline>GAME DESIGN</Overline>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">Moduly mise — příklady, ne celé know-how</h2>
    <div className="mt-10 grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-3">
      {GAME_MODULES.map((m) => (
        <article key={m.key} className="bg-surface p-6 hover:bg-elevated transition-colors">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">{m.key}</p>
          <h3 className="font-display font-bold uppercase text-white text-lg mt-2">{m.title}</h3>
          <p className="text-sm text-mist mt-2 leading-relaxed">{m.desc}</p>
        </article>
      ))}
      <article className="bg-elevated p-6 sm:col-span-2 lg:col-span-3 border-t-2 border-amber">
        <h3 className="font-display font-extrabold uppercase text-white text-xl">Scoring mise</h3>
        <p className="text-sm text-mist mt-2">Skóre není jen rychlost. Nejrychlejší jednotlivec nevyhrává — vyhrává tým, který funguje společně.</p>
        <ul className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="scoring-grid">
          {SCORING.map((s) => (
            <li key={s.label} className="bg-ink border border-line p-4">
              <span className="font-display font-extrabold text-3xl text-amber">{s.pct} %</span>
              <p className="text-sm font-bold text-white mt-1">{s.label}</p>
              <p className="text-xs text-dim mt-1">{s.desc}</p>
            </li>
          ))}
        </ul>
      </article>
    </div>
  </Wrap>
);

const PackagesBlock = () => (
  <Wrap>
    <Overline>BALÍČKY</Overline>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">Tři balíčky. Jedna úroveň.</h2>
    <div className="mt-10 grid gap-6 lg:grid-cols-3" data-testid="pricing-packages">
      {PACKAGES.map((p) => (
        <article
          key={p.id}
          className={`relative border p-7 flex flex-col ${p.popular ? "border-amber bg-elevated shadow-[0_0_40px_rgba(255,77,0,0.12)]" : "border-linestrong bg-surface"}`}
          data-testid={p.testId}
        >
          {p.popular && (
            <span className="absolute -top-3 left-6 bg-amber text-white text-[10px] font-mono font-bold uppercase tracking-[0.2em] px-3 py-1">Most popular</span>
          )}
          <h3 className="font-display font-extrabold uppercase text-white text-2xl">{p.name}</h3>
          <p className="text-xs font-mono uppercase tracking-wider text-dim mt-1">{p.duration}</p>
          <p className="text-sm text-mist mt-3">{p.tagline}</p>
          <p className="font-display font-extrabold text-amber text-2xl mt-4">{p.price}</p>
          {p.minPrice && <p className="text-xs font-mono text-dim mt-1">{p.minPrice}</p>}
          <ul className="mt-5 space-y-2 flex-1">
            {p.features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-mist">
                <Check className="w-4 h-4 text-amber mt-0.5 shrink-0" aria-hidden="true" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <CtaLink to="/poptavka" variant={p.popular ? "primary" : "ghost"} testId={`package-cta-${p.id}`} location="packages">
            Chci tento balíček
          </CtaLink>
        </article>
      ))}
    </div>
    <p className="text-xs text-dim mt-6 max-w-2xl">{PRICE_NOTE}</p>
  </Wrap>
);

const PriceTableBlock = () => (
  <Wrap>
    <Overline>ORIENTAČNÍ CENY</Overline>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">Příklady cen Mission 24</h2>
    <div className="mt-8 overflow-x-auto">
      <table className="w-full border border-line text-left" data-testid="price-table">
        <thead>
          <tr className="bg-elevated">
            <th className="px-5 py-3 text-xs font-mono uppercase tracking-wider text-mist">Počet lidí</th>
            <th className="px-5 py-3 text-xs font-mono uppercase tracking-wider text-mist">Cena akce (orientačně)</th>
            <th className="px-5 py-3 text-xs font-mono uppercase tracking-wider text-mist hidden sm:table-cell">Na osobu</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {PRICE_TABLE_24.map((r) => (
            <tr key={r.people} className="bg-surface hover:bg-elevated transition-colors" data-testid={`price-row-${r.people}`}>
              <td className="px-5 py-4 font-display font-bold text-white text-lg">{r.people} lidí</td>
              <td className="px-5 py-4 font-display font-bold text-amber text-lg">{r.price}</td>
              <td className="px-5 py-4 text-sm text-mist hidden sm:table-cell">
                {Math.round(parseInt(r.price.replace(/\D/g, ""), 10) / r.people).toLocaleString("cs-CZ")} Kč
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="text-xs text-dim mt-4 max-w-2xl">{PRICE_NOTE}</p>
  </Wrap>
);

const UpsellsBlock = () => (
  <Wrap>
    <Overline>DODATEČNÉ SLUŽBY</Overline>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">Upsilonte si misi na míru</h2>
    <div className="mt-8 grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-3" data-testid="upsells-grid">
      {UPSELLS.map((u) => (
        <div key={u.title} className="bg-surface p-5 hover:bg-elevated transition-colors">
          <p className="font-semibold text-white text-sm">{u.title}</p>
          <p className="text-xs text-dim mt-1.5 leading-relaxed">{u.desc}</p>
        </div>
      ))}
    </div>
    <p className="mt-6 text-sm text-white/90 font-medium max-w-2xl">Vyzvedneme váš tým před kanceláří a zase ho tam vrátíme.</p>
  </Wrap>
);

const SUPPLIERS = ["hotel", "autobus", "teambuildingová agentura", "event venue", "catering", "bar", "DJ"];

const SupplierBlock = () => (
  <Wrap cls="bg-surface border-y border-line max-w-none">
    <div className="max-w-7xl mx-auto">
      <Overline>JEDEN DODAVATEL</Overline>
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">Jeden dodavatel. Celá akce.</h2>
      <ul className="mt-8 flex flex-wrap gap-3" data-testid="one-supplier-list">
        {SUPPLIERS.map((s) => (
          <li key={s} className="flex items-center gap-2 border border-linestrong px-4 py-2 text-sm text-dim">
            <X className="w-4 h-4 text-amber" aria-hidden="true" />
            <span className="line-through decoration-amber/60">{s}</span>
          </li>
        ))}
      </ul>
      <p className="font-display font-extrabold uppercase text-white text-2xl sm:text-3xl mt-8">Všechno zařídíme my.</p>
    </div>
  </Wrap>
);

const AUDIENCE = [
  { t: "HR & People Ops", d: "Akce, kterou zaměstnanci opravdu chtějí — a kterou HR nemusí koordinovat měsíc." },
  { t: "Office manager", d: "Jedna poptávka, jedna faktura, jeden koordinátor. Nic víc." },
  { t: "Team leader", d: "Mise ukáže, jak tým funguje pod tlakem — bez hodnotících tabulek." },
  { t: "CEO / founder", d: "Investice, kterou tým odpracuje v komunikaci ještě měsíce po akci." },
  { t: "Sales tým", d: "Soutěživost přesměrovaná do týmového výsledku." },
  { t: "IT tým", d: "Šifry, logika a offline den, který dev tým ocení." },
  { t: "Management offsite", d: "Strategie dopoledne, mise odpoledne, Warehouse večer." },
];

const AudienceBlock = () => (
  <Wrap>
    <Overline>PRO KOHO</Overline>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">Pro týmy, které chtějí víc než večeři v zámecké restauraci</h2>
    <div className="mt-8 grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-3" data-testid="audience-grid">
      {AUDIENCE.map((a) => (
        <div key={a.t} className="bg-surface p-6 hover:bg-elevated transition-colors">
          <p className="font-display font-bold uppercase text-white">{a.t}</p>
          <p className="text-sm text-mist mt-2 leading-relaxed">{a.d}</p>
        </div>
      ))}
    </div>
  </Wrap>
);

const SAFETY_ITEMS = [
  { icon: Mountain, t: "Úrovně obtížnosti", d: "Trasa mise má varianty — program se přizpůsobí kondici týmu, ne naopak." },
  { icon: CloudRain, t: "Weather plan", d: "Varianty trasy, kryté checkpointy a indoor backup ve Warehouse. Akce se neruší." },
  { icon: PhoneCall, t: "Nouzový kontakt", d: "Každý tým má nouzový kontakt, instruktoři jsou na checkpointech po celou dobu mise." },
  { icon: HeartPulse, t: "Fyzickou část lze vynechat", d: "U jednotlivců bez vlivu na zážitek týmu — čekají je role na checkpointech i večerní program." },
  { icon: Users, t: "Program dle kondice týmu", d: "Složení skupiny zjišťujeme v poptávce a obtížnost ladíme na míru." },
  { icon: Shield, t: "Žádné nebezpečné improvizace", d: "Žádné improvizované překážky. Trasa vede značeným terénem Ještědského hřbetu." },
];

const SafetyBlock = () => (
  <Wrap>
    <Overline>BEZPEČNOST</Overline>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">Extreme experience. Safe execution.</h2>
    <div className="mt-8 grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-3" data-testid="safety-section">
      {SAFETY_ITEMS.map((s) => (
        <div key={s.t} className="bg-surface p-6">
          <s.icon className="w-5 h-5 text-amber" aria-hidden="true" />
          <p className="font-semibold text-white text-sm mt-3">{s.t}</p>
          <p className="text-sm text-mist mt-1.5 leading-relaxed">{s.d}</p>
        </div>
      ))}
    </div>
    <Link to="/bezpecnost" className="inline-flex items-center gap-2 mt-6 text-amber font-display font-bold uppercase tracking-wide hover:gap-3 transition-all" data-testid="safety-link">
      Víc o bezpečnosti <ArrowRight className="w-4 h-4" aria-hidden="true" />
    </Link>
  </Wrap>
);

const GalleryBlock = ({ b }) => (
  <Wrap cls="max-w-none px-0 sm:px-0 lg:px-0">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {b.kicker && <Overline>{b.kicker}</Overline>}
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">{b.heading}</h2>
    </div>
    <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-1" data-testid="gallery-strip">
      {GALLERY_STRIP.map((img, i) => (
        <figure key={img.src} className={i === 0 ? "col-span-2 md:col-span-1" : ""}>
          <img src={img.src} alt={img.alt} width={img.w} height={img.h} loading="lazy" decoding="async" className="w-full aspect-square object-cover" />
        </figure>
      ))}
    </div>
  </Wrap>
);

const CaseStudiesBlock = () => (
  <Wrap>
    <Overline>REFERENCE / REÁLNÉ FIRMY</Overline>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white mt-3">Případové studie</h2>
    <div className="mt-8 border border-dashed border-linestrong bg-surface p-8 sm:p-12 text-center" data-testid="case-study-placeholder">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Připravujeme — sezóna 2026</p>
      <p className="text-mist text-sm mt-4 max-w-xl mx-auto leading-relaxed">
        Publikujeme pouze reálné reference z reálných akcí — jmenovitě se souhlasem klienta, nebo anonymizovaně
        („pražská technologická firma, 42 účastníků“). Žádné vymyšlené firmy, žádná falešná hodnocení.
      </p>
      <Link to="/pripadove-studie" className="inline-flex items-center gap-2 mt-6 text-amber font-display font-bold uppercase tracking-wide hover:gap-3 transition-all" data-testid="case-studies-link">
        Víc o případových studiích <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </Link>
    </div>
  </Wrap>
);

const ContactInfoBlock = () => (
  <Wrap>
    <div className="grid sm:grid-cols-2 gap-px bg-line border border-line max-w-3xl" data-testid="contact-info">
      <div className="bg-surface p-6">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-dim">Provozovatel</p>
        <p className="text-white font-semibold mt-2">{SITE.company}</p>
        <p className="text-mist text-sm mt-1">{SITE.address}</p>
        <p className="text-mist text-sm mt-1">IČO: {SITE.ico} · DIČ: {SITE.dic}</p>
      </div>
      <div className="bg-surface p-6">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-dim">Kontakt</p>
        <a href={`mailto:${SITE.email}`} onClick={() => trackEvent("click_email", { location: "kontakt" })} className="flex items-center gap-2 text-white font-semibold mt-2 hover:text-amber transition-colors" data-testid="contact-email">
          {SITE.email}
        </a>
        <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} onClick={() => trackEvent("click_phone", { location: "kontakt" })} className="flex items-center gap-2 text-white font-semibold mt-1.5 hover:text-amber transition-colors" data-testid="contact-phone">
          {SITE.phone}
        </a>
        <p className="text-mist text-sm mt-2">Mise probíhají ve Warehouse Liberec a Ještědském hřbetu.</p>
      </div>
    </div>
  </Wrap>
);

const ProseBlock = ({ b }) => (
  <Wrap cls="pt-8">
    <div className="max-w-3xl space-y-10">
      {b.sections.map((s) => (
        <section key={s.heading}>
          <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-white">{s.heading}</h2>
          {s.paras.map((p, i) => <p key={i} className="text-sm sm:text-base text-mist mt-3 leading-relaxed">{p}</p>)}
          {s.list && (
            <ul className="mt-3 space-y-2">
              {s.list.map((li) => (
                <li key={li} className="flex items-start gap-3 text-sm sm:text-base text-mist">
                  <Check className="w-4 h-4 text-amber mt-1 shrink-0" aria-hidden="true" />
                  <span>{li}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  </Wrap>
);

const CtaBlock = ({ b }) => (
  <Wrap>
    <div className="bg-elevated border border-linestrong p-8 sm:p-12 text-center grain relative overflow-hidden">
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">{b.heading}</h2>
      {b.text && <p className="text-mist mt-3 max-w-xl mx-auto">{b.text}</p>}
      <div className="mt-6">
        <CtaLink to="/poptavka" testId="cta-block-button" location="cta_block">Chci návrh mise</CtaLink>
      </div>
    </div>
  </Wrap>
);

const RelatedBlock = ({ items }) => (
  <Wrap cls="pt-4">
    <h2 className="font-display text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-white">Mohlo by vás zajímat</h2>
    <div className="mt-6 grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4" data-testid="internal-linking-section">
      {items.slice(0, 5).map((r) => (
        <Link key={r.href} to={r.href} className="bg-surface p-5 hover:bg-elevated transition-colors group" data-testid={`related-link-${r.href.slice(1).replace(/\//g, "-") || "home"}`}>
          <p className="font-display font-bold uppercase text-white group-hover:text-amber transition-colors">{r.title}</p>
          <p className="text-xs text-dim mt-1.5">{r.text}</p>
        </Link>
      ))}
    </div>
  </Wrap>
);

const LeadFormBlock = ({ b, pagePath }) => (
  <Wrap>
    <LeadForm heading={b.heading} text={b.text} />
  </Wrap>
);

const FactsBlock = ({ b }) => (
  <Wrap cls="!pt-2">
    <dl className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line" data-testid="key-facts">
      {b.items.map(([k, v]) => (
        <div key={k} className="bg-surface p-5">
          <dt className="text-[11px] font-mono uppercase tracking-[0.2em] text-dim">{k}</dt>
          <dd className="text-sm font-semibold text-white mt-1.5">{v}</dd>
        </div>
      ))}
    </dl>
  </Wrap>
);

const BLOCKS = {
  hero: HeroBlock,
  answer: AnswerBlock,
  section: SectionBlock,
  steps: StepsBlock,
  timeline: TimelineBlock,
  game: GameBlock,
  packages: PackagesBlock,
  pricetable: PriceTableBlock,
  upsells: UpsellsBlock,
  supplier: SupplierBlock,
  audience: AudienceBlock,
  safety: SafetyBlock,
  gallery: GalleryBlock,
  casestudies: CaseStudiesBlock,
  contactinfo: ContactInfoBlock,
  prose: ProseBlock,
  faq: ({ b }) => <Wrap><Faq items={b.items} heading={b.heading} /></Wrap>,
  cta: CtaBlock,
  facts: FactsBlock,
  leadform: LeadFormBlock,
};

export const renderBlocks = (blocks, related) => (
  <>
    {blocks.map((b, i) => {
      const Cmp = BLOCKS[b.t];
      if (!Cmp) return null;
      return <Cmp key={`${b.t}-${i}`} b={b} />;
    })}
    {related && related.length > 0 && <RelatedBlock items={related} />}
  </>
);
